import { useEffect, useState } from 'react'

import { Button } from 'components/ui/button'
import { type CSVHeader, downloadCSV, generateCSV } from 'utils/csv_download'
import { useGetBrokersRequiredPayments } from 'utils/web_api'
import { type BrokersRequiredPaymentInfo } from 'utils/web_api.schemas'

import PreConfiguredQueryLoader from './PreConfiguredQueryLoader'
import QueryBuilder from './QueryBuilder'
import { useRequiredPaymentsContext } from './RequiredPaymentsContext'
import ResultsTable from './ResultsTable'

// Sanitizes a CSV cell value to prevent CSV injection attacks. Escapes
// cells that start with formula characters by prefixing with a single quote.
// See: https://owasp.org/www-community/attacks/CSV_Injection
function sanitizeCsvCell (cell: string): string {
  const dangerousChars = ['=', '+', '-', '@', '|', '%']
  if (dangerousChars.some((char) => cell.startsWith(char))) {
    return `'${cell}` // Prefix with single quote to force literal interpretation
  }
  return cell
}

type CsvRowData = Record<string, string>

function exportToCsv (
  config: CSVHeader<BrokersRequiredPaymentInfo>[],
  data: BrokersRequiredPaymentInfo[]
) {
  if (data.length === 0) return

  // Transform data to sanitized string records
  const sanitizedData: CsvRowData[] = data.map((rowData) => {
    const row: CsvRowData = {}
    config.forEach(({ key }) => {
      const colValue = rowData[key]
      let colStringValue: string
      if (colValue && typeof colValue === 'object' && 'formatted_amount' in colValue) {
        colStringValue = colValue.formatted_amount
      } else {
        colStringValue = colValue?.toString() ?? ''
      }
      row[key] = sanitizeCsvCell(colStringValue)
    })
    return row
  })

  const csvContent = generateCSV(config as unknown as CSVHeader<CsvRowData>[], sanitizedData)
  const filename = `required-payments-${new Date().toISOString().split('T')[0]}.csv`
  downloadCSV(filename, csvContent)
}

const RequiredPaymentsViewContent = () => {
  const { queryParams } = useRequiredPaymentsContext()
  const [results, setResults] = useState<BrokersRequiredPaymentInfo[]>([])
  const [error, setError] = useState<string | undefined>(undefined)

  // API query for running the query (disabled by default, triggered manually)
  const {
    data: queryResultContent,
    isLoading: isRunningQuery,
    error: queryError,
    refetch,
  } = useGetBrokersRequiredPayments(
    {
      'autopay_states[]': queryParams.autopayStates ?? undefined,
      for_month: queryParams.forMonth,
      for_year: queryParams.forYear,
      'excluded_carrier_names[]': queryParams.excludedCarrierNames ?? undefined,
      'included_carrier_names[]': queryParams.includedCarrierNames ?? undefined,
    },
    {
      query: {
        enabled: false, // Don't run automatically
      },
    }
  )

  // Update results when query succeeds
  useEffect(() => {
    if (queryResultContent) {
      setResults(queryResultContent.data)
      setError(undefined)
    }
  }, [queryResultContent])

  // Update error when query fails
  useEffect(() => {
    if (queryError) {
      const errorMessage = queryError instanceof Error ? queryError.message : 'Failed to fetch required payments'
      setError(errorMessage)
      setResults([])
    }
  }, [queryError])

  const handleRunQuery = () => {
    setError(undefined)
    refetch()
  }

  const handleExportToCsv = () => {
    exportToCsv(
      [
        { key: 'enrollment_id', label: 'Enrollment ID' },
        { key: 'member_name', label: 'Member Name' },
        { key: 'member_id', label: 'Member ID' },
        { key: 'employer_name', label: 'Employer Name' },
        { key: 'carrier_name', label: 'Carrier Name' },
        { key: 'payment_method', label: 'Payment Method' },
        { key: 'through_month', label: 'Through Month' },
        { key: 'through_year', label: 'Through Year' },
        { key: 'amount_due', label: 'Amount Due' },
      ],
      results
    )
  }

  const inputsValid = queryParams.autopayStates.length > 0
  const canRunQuery = !isRunningQuery && inputsValid

  return (
    <div className='max-w-[2400px] mx-auto px-4 pb-6'>
      <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2400px] mx-auto'>

        <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
          <div className='text-2xl font-semibold w-96'>Required Premium Payments</div>
        </div>

        <div className='p-4 border rounded-lg'>
          <div className='text-lg font-medium mb-4'>Pre-configured Queries</div>
          <PreConfiguredQueryLoader />
        </div>

        <div className='p-4 border rounded-lg'>
          <div className='text-lg font-medium mb-4'>Query Parameters</div>
          <QueryBuilder />
        </div>

        {/* Actions Section */}
        <div className='flex justify-end gap-x-2'>
          <Button
            onClick={handleRunQuery}
            disabled={!canRunQuery}
          >
            {isRunningQuery ? 'Running...' : 'Run Query'}
          </Button>
          <Button variant='secondary' disabled={results.length === 0} onClick={handleExportToCsv}>
            Export to CSV
          </Button>
        </div>

        {/* Results Section */}
        <div className='p-4 border rounded-lg'>
          <div className='text-lg font-medium mb-4'>Results</div>
          <ResultsTable results={results} isLoading={isRunningQuery} error={error} />
        </div>

      </div>
    </div>
  )
}

export default RequiredPaymentsViewContent
